import React, { useState, useEffect, useRef } from "react";
import {
  Plus,
  Trash2,
  Upload,
  AlertTriangle,
  Eye,
  X,
  FileEdit,
} from "lucide-react";
import { showSuccessToast, showErrorToast } from "../../../utils/toastUtils";
import ReactQuill from "react-quill";

const UnifiedApiParameterBuilder = ({
  value,
  onChange,
  label = "API Parameters",
}) => {
  const [isMethod, setIsMethod] = useState(false);
  const [simpleParams, setSimpleParams] = useState([]);
  const [methods, setMethods] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [showClearModal, setShowClearModal] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const isUpdating = useRef(false);
  const [showHelpTextModal, setShowHelpTextModal] = useState(false);
  const [selectedParam, setSelectedParam] = useState(null);

  // Helper: Check if an object looks like a parameter definition
  const isParamObject = (obj) => {
    return (
      obj &&
      typeof obj === "object" &&
      (obj.hasOwnProperty("is_required") || obj.hasOwnProperty("is_readonly"))
    );
  };

  // Helper: Check if an object is a method (contains parameters)
  const isMethodObject = (obj) => {
    if (!obj || typeof obj !== "object") return false;
    const values = Object.values(obj);
    return values.length > 0 && values.every((v) => isParamObject(v));
  };

  // Parse parameters from object
  const parseParams = (params) => {
    return Object.entries(params).map(([k, v]) => ({
      name: k,
      req:
        v.is_required === 1 || v.is_required === "1" || v.is_required === true,
      ro:
        v.is_readonly === 1 || v.is_readonly === "1" || v.is_readonly === true,

      id: `${Date.now()}_${Math.random()}`,
      helpText: v.help_text || "",
    }));
  };
  // help_text added for the filed

  // Parse and initialize from value prop
  useEffect(() => {
    if (!value || value.trim() === "" || isUpdating.current) return;

    try {
      // Clean escaped characters
      const cleanValue = value
        .replace(/\\r\\n/g, "")
        .replace(/\\n/g, "")
        .replace(/\\/g, "")
        .replace(/,(\s*[}\]])/g, "$1"); // Remove trailing commas

      const parsed = JSON.parse(cleanValue);

      // Case 1: Has methods object with nested methods (CORRECT FORMAT)
      if (parsed.methods && typeof parsed.methods === "object") {
        setIsMethod(true);
        const methodList = [];

        // Get methods from methods object
        Object.entries(parsed.methods).forEach(([name, params]) => {
          if (isMethodObject(params)) {
            methodList.push({
              name,
              params: parseParams(params),
              id: `${Date.now()}_${Math.random()}_${name}`,
            });
          }
        });

        //  FIX: Check for misplaced methods at root level (INCORRECT FORMAT from backend)
        Object.entries(parsed).forEach(([key, val]) => {
          if (key !== "methods" && isMethodObject(val)) {
            console.warn(
              `Found method "${key}" at root level, should be in methods object`,
            );
            methodList.push({
              name: key,
              params: parseParams(val),
              id: `${Date.now()}_${Math.random()}_${key}`,
            });
          }
        });

        setMethods(methodList);
        setActiveIdx(0);
        console.log(
          "Loaded methods:",
          methodList.map((m) => m.name),
        );
      }
      // Case 2: Simple structure (no methods key)
      else if (Object.keys(parsed).length > 0) {
        // Check if all entries are parameters
        const allParams = Object.values(parsed).every((v) => isParamObject(v));

        if (allParams) {
          // Simple parameter structure
          setIsMethod(false);
          setSimpleParams(parseParams(parsed));
        } else {
          // Multiple methods at root level (INCORRECT FORMAT)
          setIsMethod(true);
          const methodList = [];

          Object.entries(parsed).forEach(([key, val]) => {
            if (isMethodObject(val)) {
              methodList.push({
                name: key,
                params: parseParams(val),
                id: `${Date.now()}_${Math.random()}_${key}`,
              });
            }
          });

          if (methodList.length > 0) {
            setMethods(methodList);
            setActiveIdx(0);
            console.log(
              "Loaded methods from root:",
              methodList.map((m) => m.name),
            );
          }
        }
      }

      setInitialized(true);
    } catch (e) {
      console.error("Parse error:", e, "Value:", value);
      setInitialized(true);
    }
  }, [value]);

  // Convert to JSON and update parent - with flicker prevention
  useEffect(() => {
    if (!initialized || isUpdating.current) return;

    isUpdating.current = true;

    const output = isMethod
      ? {
          methods: methods.reduce((acc, m) => {
            if (m.name.trim()) {
              acc[m.name] = m.params.reduce((p, x) => {
                if (x.name.trim()) {
                  p[x.name] = {
                    is_required: x.req ? 1 : 0,
                    is_readonly: x.ro ? 1 : 0,
                    help_text: x.helpText || "",
                  };
                }
                return p;
              }, {});
            }
            return acc;
          }, {}),
        }
      : simpleParams.reduce((acc, x) => {
          if (x.name.trim()) {
            acc[x.name] = {
              is_required: x.req ? 1 : 0,
              is_readonly: x.ro ? 1 : 0,
              help_text: x.helpText || "",
            };
          }
          return acc;
        }, {});

    const json = JSON.stringify(output, null, 2);

    // Only update if changed (prevent flickering)
    const cleanValue = value
      ?.replace(/\\r\\n|\\n|\\/g, "")
      .replace(/,(\s*[}\]])/g, "$1")
      .replace(/\s/g, "");
    const cleanJson = json.replace(/\s/g, "");

    if (cleanJson !== cleanValue) {
      onChange(json);
    }

    setTimeout(() => {
      isUpdating.current = false;
    }, 100);
  }, [simpleParams, methods, isMethod, initialized]);

  // Handlers
  const add = () => {
    const newParam = {
      name: "",
      req: true,
      ro: false,
      helpText: "",
      id: `${Date.now()}_${Math.random()}`,
    };
    if (isMethod) {
      if (!methods[activeIdx]) return;
      const updated = [...methods];
      updated[activeIdx] = {
        ...updated[activeIdx],
        params: [...updated[activeIdx].params, newParam],
      };
      setMethods(updated);
    } else {
      setSimpleParams([...simpleParams, newParam]);
    }
  };

  const remove = (id) => {
    if (isMethod) {
      const updated = [...methods];
      updated[activeIdx] = {
        ...updated[activeIdx],
        params: updated[activeIdx].params.filter((p) => p.id !== id),
      };
      setMethods(updated);
    } else {
      setSimpleParams(simpleParams.filter((p) => p.id !== id));
    }
  };

  const update = (id, field, val) => {
    if (isMethod) {
      const updated = [...methods];
      updated[activeIdx] = {
        ...updated[activeIdx],
        params: updated[activeIdx].params.map((p) =>
          p.id === id ? { ...p, [field]: val } : p,
        ),
      };
      setMethods(updated);
    } else {
      setSimpleParams(
        simpleParams.map((p) => (p.id === id ? { ...p, [field]: val } : p)),
      );
    }
  };

  const addMethod = () => {
    const newMethod = {
      name: "",
      params: [],
      id: `${Date.now()}_${Math.random()}`,
    };
    setMethods([...methods, newMethod]);
    setActiveIdx(methods.length);
  };

  const removeMethod = (id) => {
    const updated = methods.filter((m) => m.id !== id);
    setMethods(updated);
    if (activeIdx >= updated.length) {
      setActiveIdx(Math.max(0, updated.length - 1));
    }
  };

  const updateMethodName = (id, name) => {
    setMethods(methods.map((m) => (m.id === id ? { ...m, name } : m)));
  };

  const toggleMode = () => {
    if (!isMethod && simpleParams.length > 0) {
      setMethods([
        { name: "method1", params: simpleParams, id: `${Date.now()}` },
      ]);
      setSimpleParams([]);
    } else if (isMethod && methods.length > 0) {
      setSimpleParams(methods[0]?.params || []);
      setMethods([]);
    }
    setIsMethod(!isMethod);
  };

  const importJSON = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);

        if (parsed.methods) {
          setIsMethod(true);
          const methodList = Object.entries(parsed.methods).map(
            ([name, params]) => ({
              name,
              params: parseParams(params),
              id: `${Date.now()}_${Math.random()}_${name}`,
            }),
          );
          setMethods(methodList);
          setActiveIdx(0);
        } else {
          const allParams = Object.values(parsed).every((v) =>
            isParamObject(v),
          );

          if (allParams) {
            setIsMethod(false);
            setSimpleParams(parseParams(parsed));
          } else {
            // Methods at root level
            setIsMethod(true);
            const methodList = Object.entries(parsed)
              .filter(([_, val]) => isMethodObject(val))
              .map(([name, params]) => ({
                name,
                params: parseParams(params),
                id: `${Date.now()}_${Math.random()}_${name}`,
              }));
            setMethods(methodList);
            setActiveIdx(0);
          }
        }

        showSuccessToast("Imported successfully");
      } catch (err) {
        console.error("Import error:", err);
        showErrorToast("Invalid JSON");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleClearAll = () => {
    setSimpleParams([]);
    setMethods([]);
    setShowClearModal(false);
    showSuccessToast("Cleared successfully");
  };

  const params = isMethod ? methods[activeIdx]?.params || [] : simpleParams;
  const current = methods[activeIdx];

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium">{label}</label>
        <div className="flex gap-2 text-xs">
          <button
            type="button"
            onClick={toggleMode}
            className="px-2 py-1 bg-purple-100 hover:bg-purple-200 rounded transition-colors"
          >
            {isMethod ? "📁 Methods" : "📄 Simple"}
          </button>
          <label className="px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded cursor-pointer transition-colors">
            <Upload size={14} className="inline mr-1" />
            Import
            <input
              type="file"
              accept=".json"
              onChange={importJSON}
              className="hidden"
            />
          </label>
          <button
            type="button"
            onClick={() => setShowClearModal(true)}
            className="px-2 py-1 bg-red-100 hover:bg-red-200 rounded transition-colors"
            disabled={simpleParams.length === 0 && methods.length === 0}
          >
            Clear
          </button>
        </div>
      </div>

      {/* Method Tabs */}
      {isMethod && (
        <div className="mb-2 border rounded bg-white">
          <div className="flex gap-2 p-2 bg-gray-50 border-b overflow-x-auto">
            {methods.map((m, i) => (
              <div
                key={m.id}
                className="flex items-center gap-1 bg-white border rounded px-2 py-1 flex-shrink-0"
              >
                <button
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  className={`text-xs whitespace-nowrap ${activeIdx === i ? "font-bold text-blue-600" : "text-gray-600"}`}
                >
                  {m.name || `Method ${i + 1}`}
                </button>
                <button
                  type="button"
                  onClick={() => removeMethod(m.id)}
                  className="text-red-600 p-0.5 hover:bg-red-50 rounded"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addMethod}
              className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded flex items-center gap-1 flex-shrink-0 transition-colors"
            >
              <Plus size={12} />
              Add
            </button>
          </div>
          {current && (
            <div className="p-2 border-b bg-gray-50">
              <input
                type="text"
                value={current.name}
                onChange={(e) => updateMethodName(current.id, e.target.value)}
                placeholder="Method name (e.g., generateToken, fetchDetails)"
                className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>
      )}

      {/* Parameters Table */}
      <div className="border rounded p-2 bg-white">
        {params.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">
            <p>No parameters added yet</p>
            <p className="text-xs mt-1">Click "Add Parameter" to start</p>
          </div>
        ) : (
          <div className="space-y-1">
            <div className="grid grid-cols-12 gap-1 text-xs font-semibold text-gray-700 pb-1 border-b">
              <div className="col-span-2">Field Name</div>
              <div className="col-span-2 text-center">Required</div>
              <div className="col-span-2 text-center">Readonly</div>
              <div className="col-span-5 text-center">Help Text</div>
              <div className="col-span-1">Actions</div>
            </div>

            {params.map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-12 gap-1 items-center bg-gray-50 p-1 rounded"
              >
                <div className="col-span-2">
                  <input
                    type="text"
                    value={p.name}
                    onChange={(e) => update(p.id, "name", e.target.value)}
                    placeholder="e.g., api_key, mobile_number"
                    className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
                <div className="col-span-2 flex justify-center">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={p.req}
                      onChange={(e) => update(p.id, "req", e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span
                      className={`text-xs ${p.req ? "text-green-700 font-semibold" : "text-gray-400"}`}
                    >
                      {p.req ? "Yes" : "No"}
                    </span>
                  </label>
                </div>
                <div className="col-span-2 flex justify-center">
                  <label className="flex items-center gap-1 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={p.ro}
                      onChange={(e) => update(p.id, "ro", e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span
                      className={`text-xs ${p.ro ? "text-orange-700 font-semibold" : "text-gray-400"}`}
                    >
                      {p.ro ? "Yes" : "No"}
                    </span>
                  </label>
                </div>

                {/* String | 10 numerical digits | Regex: ^(?:\+91\s?|0)?[6-9]\d{9}$ */}
                {/* Integer | 1 = Name lookup required, 0 = Not required */}

                <div className="col-span-5 flex justify-center">
                  <input
                    type="text"
                    value={p.helpText || ""}
                    onChange={(e) => update(p.id, "helpText", e.target.value)}
                    placeholder="Description / Regex / Notes"
                    className="w-full px-2 py-1 text-xs border rounded"
                  />
                </div>
                <div className="col-span-1 ml-2 px-4 flex justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowHelpTextModal(true);
                      setSelectedParam(p);
                    }}
                    className="p-1 text-green-600 hover:bg-green-700 hover:text-white rounded hover:transition-all hover:duration-300"
                  >
                    <FileEdit size={20} />
                  </button>

                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    className="p-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showHelpTextModal && selectedParam && (
          <HelpTextModal
            param={selectedParam}
            onClose={() => setShowHelpTextModal(false)}
            setValue={update}
          />
        )}

        <button
          type="button"
          onClick={add}
          disabled={isMethod && !current}
          className="mt-2 w-full px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded flex items-center justify-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus size={14} />
          Add Parameter
        </button>
      </div>

      {/* JSON Preview */}
      <div className="mt-2">
        <label className="text-xs font-medium text-gray-700 mb-1 block">
          JSON Preview
        </label>
        <pre className="bg-gray-900 text-green-400 p-2 rounded text-xs max-h-48 overflow-auto font-mono">
          {isMethod
            ? JSON.stringify(
                {
                  methods: methods.reduce((acc, m) => {
                    if (m.name.trim()) {
                      acc[m.name] = m.params.reduce((p, x) => {
                        if (x.name.trim())
                          p[x.name] = {
                            is_required: x.req ? 1 : 0,
                            is_readonly: x.ro ? 1 : 0,
                            help_text: x.helpText || "",
                          };
                        return p;
                      }, {});
                    }
                    return acc;
                  }, {}),
                },
                null,
                2,
              )
            : JSON.stringify(
                simpleParams.reduce((acc, x) => {
                  if (x.name.trim())
                    acc[x.name] = {
                      is_required: x.req ? 1 : 0,
                      is_readonly: x.ro ? 1 : 0,
                      help_text: x.helpText || "",
                    };
                  return acc;
                }, {}),
                null,
                2,
              )}
        </pre>
      </div>

      {/* Clear Modal */}
      {showClearModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-sm w-full mx-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="text-red-600 flex-shrink-0" size={24} />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900 mb-1">
                  Clear All Parameters?
                </h3>
                <p className="text-sm text-gray-600">
                  This action cannot be undone. All parameters will be
                  permanently removed.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <button
                type="button"
                onClick={() => setShowClearModal(false)}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedApiParameterBuilder;

const HelpTextModal = ({ setValue, param, onClose }) => {
  const modules_quill = {
    toolbar: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["link", "image", "video"],
      [{ color: ["#ff0000", "#00ff00", "#0000ff", "#000000"] }],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "indent",
    "link",
    "image",
    "video",
    "color",
  ];
  const [message, setMessage] = useState(param.helpText);

  const onSave = () => {
    setValue(param.id, "helpText", message);
    onClose();
  };

  // const [value, setValue] = useState(null);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm transition-opacity p-4">
      {/* Modal Container */}
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-semibold tracking-tight text-slate-800">
            Write Message
          </h2>
          <button
            type="button"
            className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-200"
            aria-label="Close"
          >
            <X onClick={onClose} size={20} strokeWidth={2.5} />
          </button>
        </div>

        {/* Body / Editor */}
        <div className="flex flex-col w-full p-6">
          <div className="relative rounded-md border-slate-200 shadow-sm">
            <ReactQuill
              theme="snow"
              value={message || param.helpText || ""}
              onChange={(content) => setMessage(content)}
              modules={modules_quill}
              formats={formats}
              placeholder="Enter your message here..."
              /* Increased height and customized wrapper */
              className="h-64 w-full [&_.ql-container]:rounded-b-lg [&_.ql-container]:text-base [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:border-slate-200 [&_.ql-container]:border-slate-200"
            />
          </div>
        </div>

        {/* Footer Actions (Optional but recommended) */}
        {/* Note: margin-top added to account for the ReactQuill fixed height */}
        <div className="flex items-center justify-end gap-3 bg-slate-50 px-6 py-4 mt-12 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-200"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Message
          </button>
        </div>
      </div>
    </div>
  );
};
